import { useQuery } from '@tanstack/react-query'
import { UNIFIED_DOORS_SUMMARY_KEY } from 'constants/tableQueryKeys'
import { useMemo } from 'react'
import useApiService from 'services/api'
import { getTargetType } from './targetTypeMap'
import { adaptUnifiedListParams } from './unifiedResponseAdapter'
import { adaptUnifiedDoorItemForVertical } from './unifiedDoorResponseAdapter'

/**
 * Restaurant doors (DoorsTableComponentV2) unified data source (FIR-6995,
 * default-on since the FIR-6997 cutover).
 *
 * The door analog of `useUnifiedChainsTable`. The restaurant-doors table is the
 * one door table that does NOT use `DECommonTable` — it has a bespoke component
 * (`DoorsTableComponentV2`) with elastic name/menu search, a product selector,
 * a rich LTV summary header (`total_ltv`, `places_count`), and a column set
 * (`DoorsTableColumnsV2`) far wider than what the unified doors endpoint
 * provides. So instead of the generic `useUnifiedDoorsTableActions` hook, this
 * returns the pieces the bespoke component needs:
 *
 *  - `listAction`: a bound list fetcher with a
 *    `(params, signal) => Promise<{ results }>` shape, with each row
 *    re-nested under `restaurant` (`restaurant.firstbite_id` = unified
 *    `detail_id`, `restaurant.<segment>` from the provider), and
 *    `chain_proxy.id` / `door_proxy_id` set so the existing columns, detail
 *    links, selection (keyed off `restaurant.firstbite_id`), and row actions
 *    (`chain_proxy.id`, `door_proxy_id`) resolve.
 *  - `unifiedCount`: the unified door count for the current filters, to drive
 *    pagination + `setTotalRowsInBackend` so paging matches the unified rows.
 *
 * The V2-only columns the unified endpoint does not feed (instagram, address
 * demographics, CRM-report columns) are intentionally left to the cells'
 * existing `?? 0` / `'-'` fallbacks — the table renders blanks for them
 * rather than crashing.
 *
 * LTV rollups (`total_ltv_sum`, `brand_ltv_sum`, etc.) ARE fed per-row by the
 * unified endpoint (`RestaurantDoorDetailProvider`, FIR-7837 follow-up) —
 * not one of the still-blank columns above.
 */

// DoorsTableColumnsV2 nests door detail under `restaurant` and reads
// `restaurant.firstbite_id` (= unified `detail_id`) for the detail link +
// selection; row actions key off `chain_proxy.id` and `door_proxy_id`.
const RESTAURANT_DOOR_ACCESSOR = 'restaurant'

type ApiService = ReturnType<typeof useApiService>

/**
 * Fetch one page of restaurant doors from the unified doors endpoint, with each
 * row adapted to the shape the restaurant-doors consumers read: door detail
 * nested under `restaurant` (`restaurant.firstbite_id` = unified `detail_id`)
 * plus a top-level `door_proxy_id`. Shared by `useUnifiedDoorsReportsTable`
 * (the doors target table) and `MobileDoorsListPage`.
 */
export async function fetchUnifiedRestaurantDoors(
  api: ApiService,
  params: Record<string, any>,
  signal?: AbortSignal
): Promise<{ results: any[] }> {
  const targetType = getTargetType('restaurant')
  const res = await api.getOpportunityDoorsList(
    targetType,
    adaptUnifiedListParams(params, targetType),
    signal
  )
  return {
    ...res,
    results: res.results.map((item) => {
      const row = adaptUnifiedDoorItemForVertical(
        item as Record<string, unknown>,
        {
          accessor: RESTAURANT_DOOR_ACCESSOR,
          // DoorsTableColumnsV2 detail link + DoorsTableComponentV2
          // selection read `restaurant.firstbite_id`, not `restaurant.id`.
          detailLinkIdField: 'firstbite_id',
        }
      )
      // V2 keys door-level actions off a top-level `door_proxy_id`
      // (DECommonTable door tables read `id`); set both so selection,
      // tag/distributor/GPO assignment, and reminders resolve.
      return { ...row, door_proxy_id: row['id'] }
    }),
  } as { results: any[] }
}

export function useUnifiedDoorsReportsTable(
  filterParams: Record<string, any>,
  // Gate for the summary query (doors tab active or a chain filter present).
  // Prevents the summary from firing/forwarding stats for a tab the user
  // isn't viewing.
  summaryEnabled = true
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: any[] }>
  unifiedCount: number | undefined
  unifiedChainsCount: number | undefined
  unifiedLtv: number | undefined
  unifiedSummaryFetching: boolean
} {
  const api = useApiService()

  const listAction = useMemo(() => {
    return (params: Record<string, any>, signal?: AbortSignal) =>
      fetchUnifiedRestaurantDoors(api, params, signal)
  }, [api])

  const { data: unifiedSummary, isFetching: unifiedSummaryFetching } = useQuery(
    {
      // Use the default (finite, 0) staleTime so the count refetches after
      // mutations that invalidate UNIFIED_DOORS_SUMMARY_KEY. Do NOT set
      // `staleTime: Infinity` here: an infinite staleTime would leave the
      // count stale until a hard reload.
      enabled: summaryEnabled,
      queryKey: [UNIFIED_DOORS_SUMMARY_KEY, filterParams],
      queryFn: async ({ signal }) =>
        api.getOpportunityDoorsSummary(
          getTargetType('restaurant'),
          adaptUnifiedListParams(filterParams, getTargetType('restaurant')),
          signal
        ),
    }
  )

  return {
    listAction,
    // The full stat-card set for the filtered doors, all from the unified doors
    // summary (filter-scoped and product-aware). See DoorsTableComponentV2.
    unifiedCount: unifiedSummary?.count,
    unifiedChainsCount: unifiedSummary?.alt_count,
    unifiedLtv: unifiedSummary?.total_ltv,
    unifiedSummaryFetching,
  }
}
